import styled from '@xstyled/styled-components';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { t } from 'ttag';
import ChevronIcon from '~/assets/icons/chevron.svg';
import MoonIcon from '~/assets/icons/moon.svg';
import { select } from '~/lib/select';
import { languageName } from '~/lib/translations/languageName';
import { CloseButton } from '~/ui/buttons/CloseButton';
import { Capsule, LanguageCapsule, PlaybackSpeedCapsule } from '~/ui/capsules';
import { Flex } from '~/ui/flex/Flex';
import { DarkThemeLogo, Logo } from '~/ui/logo/Logo';
import { ToggleSwitch } from '~/ui/toggle/ToggleSwitch';
import { TrainingState, useTrainingStore } from '../state';
import { HeaderBlock } from './Header';
import { formatSpeed } from './playback-speed/playbackSpeeds';
import { SettingsPages, useSettingsStore } from './Store';
import { useToggleAudioEffect } from '~/ui/audio-effect/useToggleAudioEffect';
import { useUnlockAudio } from '~/ui/video-player/MediaPlayerProvider';

const MenuItem = styled.buttonBox`
  border: none;
  background: none;
  display: flex;
  align-items: center;
  min-width: 230px;
  padding: 0;
  color: ${p => p.theme.colors.black};
  border-radius: 4px;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent-dk);
    outline-offset: 2px;
  }

  & + & {
    margin-top: 20px;
  }
`;
MenuItem.defaultProps = {
  fontSize: { _: 'md', sm: 'sm' }
};

const MenuItemBox = styled.divBox`
  border: none;
  background: none;
  display: flex;
  align-items: center;
  min-width: 230px;
  padding: 0;
  color: ${p => p.theme.colors.black};
  margin: 20px 0;
`;
MenuItemBox.defaultProps = {
  fontSize: { _: 'md', sm: 'sm' }
};

const MenuItemLabel = styled.span`
  margin-left: 15px;
  flex: 1;
  text-align: left;
`;

const Chevron = styled(ChevronIcon).attrs({
  'aria-hidden': true,
  focusable: false
})`
  margin-left: 8px;
  stroke: var(--color-black);
`;

const HeaderTitle = styled.div`
  font-family: Recoleta;
  font-size: lg;
  color: black;
  width: 100%;
  text-align: center;
  margin-top: 3px;
`;

const selector = (s: TrainingState) =>
  select(
    s,
    'textLocale',
    'mediumLocale',
    'playbackSpeed',
    'subtitles',
    'setSubtitles',
    'logoUrl',
    'companyId',
    'auditoryFeedback',
    'setAuditoryFeedback',
    'visualFeedback',
    'setVisualFeedback',
    'darkMode',
    'setDarkMode',
    'setAutoPlay',
    'autoPlay',
    'subtitleLocale'
  );

export const Root = ({ headerTitle }: { headerTitle?: string }) => {
  const {
    textLocale,
    mediumLocale,
    playbackSpeed,
    subtitles,
    setSubtitles,
    logoUrl,
    companyId,
    auditoryFeedback,
    setAuditoryFeedback,
    visualFeedback,
    setVisualFeedback,
    darkMode,
    autoPlay,
    setAutoPlay,
    subtitleLocale
  } = useTrainingStore(selector);

  const { page, setPage, toggle, returnFocusTo } = useSettingsStore();
  const [caption, toggleCaption] = useState<boolean>(subtitles);
  const [audioFeedback, toggleAudioFeedback] =
    useState<boolean>(auditoryFeedback);
  const [visualEffects, toggleVisualEffects] =
    useState<boolean>(visualFeedback);
  const [_autoPlay, toggleAutoPlay] = useState<boolean>(autoPlay || false);

  const [playAudioEffect] = useToggleAudioEffect();
  const unlockAudio = useUnlockAudio();

  const audioRef = useRef<HTMLButtonElement>(null);
  const textRef = useRef<HTMLButtonElement>(null);
  const subtitleRef = useRef<HTMLButtonElement>(null);
  const speedRef = useRef<HTMLButtonElement>(null);
  const themeRef = useRef<HTMLButtonElement>(null);

  // Restore focus before paint. Keep returnFocusTo set so CloseButton does not
  // regain autoFocus and steal focus after restore.
  useLayoutEffect(() => {
    if (page !== SettingsPages.Root || returnFocusTo == null) return;

    const targetByPage: Partial<
      Record<SettingsPages, React.RefObject<HTMLButtonElement>>
    > = {
      [SettingsPages.MediumLocale]: audioRef,
      [SettingsPages.TextLocale]: textRef,
      [SettingsPages.SubtitleLocale]: subtitleRef,
      [SettingsPages.Speed]: speedRef,
      [SettingsPages.DarkMode]: themeRef
    };

    targetByPage[returnFocusTo]?.current?.focus();
  }, [page, returnFocusTo]);

  if (page !== SettingsPages.Root) return null;

  let showDarkLogo = false;

  if (
    darkMode === 'dark' ||
    (darkMode === 'automatic' &&
      window?.matchMedia('(prefers-color-scheme: dark)')?.matches)
  ) {
    showDarkLogo = true;
  }

  const getHeader = () => {
    if (headerTitle) {
      return <HeaderTitle>{headerTitle}</HeaderTitle>;
    } else if (companyId == '3983') {
      return <Logo src={logoUrl} height={36} title="Easyllama" />;
    } else if (showDarkLogo) {
      return <DarkThemeLogo height={36} title="Easyllama" />;
    } else {
      return <Logo height={36} title="Easyllama" />;
    }
  };

  return (
    <>
      <CloseButton
        autoFocus={returnFocusTo == null}
        onClick={() => toggle(false)}
      />
      <Flex variant="list" className="modalStyleForDarkTheme">
        <HeaderBlock>{getHeader()}</HeaderBlock>
        <MenuItem
          ref={audioRef}
          onClick={() => setPage(SettingsPages.MediumLocale)}
        >
          <LanguageCapsule highlighted />
          <MenuItemLabel>{t`Audio:`}</MenuItemLabel>
          <span>{languageName(mediumLocale)}</span>
          <Chevron />
        </MenuItem>
        <MenuItem
          ref={textRef}
          onClick={() => setPage(SettingsPages.TextLocale)}
        >
          <LanguageCapsule highlighted />
          <MenuItemLabel>{t`On-Screen Text:`}</MenuItemLabel>
          <span>{languageName(textLocale)}</span>
          <Chevron />
        </MenuItem>
        <MenuItem
          ref={subtitleRef}
          onClick={() => setPage(SettingsPages.SubtitleLocale)}
        >
          <LanguageCapsule highlighted />
          <MenuItemLabel>{t`Subtitles:`}</MenuItemLabel>
          <span>{languageName(subtitleLocale)}</span>
          <Chevron />
        </MenuItem>
        <MenuItemBox
          onKeyPress={e => {
            if (e.key === 'Enter') {
              audioFeedback &&
                (() => {
                  try {
                    playAudioEffect();
                  } catch (error) {
                    // Silently ignore audio effect errors
                  }
                })();
              toggleCaption(!caption);
              setSubtitles(!caption);
            }
          }}
        >
          <Capsule highlighted fontWeight="bold" aria-hidden="true">
            cc
          </Capsule>
          <MenuItemLabel id="captions">
            <label htmlFor="cc">{t`Subtitles:`}</label>
          </MenuItemLabel>
          <ToggleSwitch
            id="cc"
            name="cc"
            value={subtitles ? t`On` : t`Off`}
            checked={caption}
            onChange={() => {
              toggleCaption(!caption);
              setSubtitles(!caption);
            }}
            showLabel={false}
          />
        </MenuItemBox>
        <MenuItem ref={speedRef} onClick={() => setPage(SettingsPages.Speed)}>
          <PlaybackSpeedCapsule highlighted />
          <MenuItemLabel>{t`Playback Speed:`}</MenuItemLabel>
          <span>{formatSpeed(playbackSpeed)}</span>
          <Chevron />
        </MenuItem>
        <MenuItem
          ref={themeRef}
          onClick={() => setPage(SettingsPages.DarkMode)}
        >
          <Capsule highlighted fontWeight="bold" aria-hidden="true">
            <MoonIcon aria-hidden="true" focusable="false" />
          </Capsule>
          <MenuItemLabel>{t`Theme:`}</MenuItemLabel>
          <Chevron />
        </MenuItem>
        <MenuItemBox
          style={{ marginBottom: '0px' }}
          onKeyPress={e => {
            if (e.key === 'Enter') {
              audioFeedback &&
                (() => {
                  try {
                    playAudioEffect();
                  } catch (error) {
                    // Silently ignore audio effect errors
                  }
                })();
              setAuditoryFeedback(!audioFeedback);
              toggleAudioFeedback(!audioFeedback);
            }
          }}
        >
          <PlaybackSpeedCapsule highlighted />
          <MenuItemLabel id="sound effects">
            <label htmlFor="auditoryFeedback">{t`Sound Effects:`}</label>
          </MenuItemLabel>
          <ToggleSwitch
            id="auditoryFeedback"
            name="auditoryFeedback"
            value={audioFeedback ? t`On` : t`Off`}
            checked={audioFeedback}
            onChange={() => {
              setAuditoryFeedback(!audioFeedback);
              toggleAudioFeedback(!audioFeedback);
            }}
            showLabel={false}
          />
        </MenuItemBox>
        <MenuItemBox
          style={{ marginBottom: '0px' }}
          onKeyPress={e => {
            if (e.key === 'Enter') {
              audioFeedback &&
                (() => {
                  try {
                    playAudioEffect();
                  } catch (error) {
                    // Silently ignore audio effect errors
                  }
                })();
              setVisualFeedback(!visualEffects);
              toggleVisualEffects(!visualEffects);
            }
          }}
        >
          <PlaybackSpeedCapsule highlighted />
          <MenuItemLabel id="visual effects">
            <label htmlFor="visualFeedback">{t`Animations:`}</label>
          </MenuItemLabel>
          <ToggleSwitch
            id="visualFeedback"
            name="visualFeedback"
            value={visualEffects ? t`On` : t`Off`}
            checked={visualEffects}
            onChange={() => {
              setVisualFeedback(!visualEffects);
              toggleVisualEffects(!visualEffects);
            }}
            showLabel={false}
          />
        </MenuItemBox>
        <MenuItemBox
          onKeyPress={e => {
            if (e.key === 'Enter') {
              _autoPlay &&
                (() => {
                  try {
                    playAudioEffect();
                  } catch (error) {
                    // Silently ignore audio effect errors
                  }
                })();
              // Turning auto-play on is the earliest gesture we can use to let
              // WebKit play narration on its own from here on.
              if (!_autoPlay) unlockAudio();
              setAutoPlay(!_autoPlay);
              toggleAutoPlay(!_autoPlay);
            }
          }}
        >
          <PlaybackSpeedCapsule highlighted />
          <MenuItemLabel id="auto play">
            <label htmlFor="autoPlay">{t`Auto Play:`}</label>
          </MenuItemLabel>
          <ToggleSwitch
            id="autoPlay"
            name="autoPlay"
            value={_autoPlay ? t`On` : t`Off`}
            checked={_autoPlay}
            onChange={() => {
              if (!_autoPlay) unlockAudio();
              setAutoPlay(!_autoPlay);
              toggleAutoPlay(!_autoPlay);
            }}
            showLabel={false}
          />
        </MenuItemBox>
      </Flex>
    </>
  );
};
